import { NgIf, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ContentChild,
  EventEmitter,
  Input,
  Output,
  TemplateRef,
} from '@angular/core';
import { KanbanSkeletonComponent } from '@shared/components/kanban-skeleton/kanban-skeleton.component';
import { VaultFormatCurrencyPipe } from '@shared/pipes/vault-format-currency.pipe';
import { DndDropEvent, DndModule } from 'ngx-drag-drop';
import {
  KanbanBoardColumn,
  KanbanBoardItem,
  KanbanCardTemplateContext,
  KanbanColumnMinimizeEvent,
  KanbanColumnTemplateContext,
  KanbanColumnTotal,
  KanbanItemDragStartEvent,
  KanbanItemDropEvent,
} from './kanban-board.types';

/**
 * Board de colunas arrastável, só a parte visual (card 3149).
 *
 * Extraído do kanban do Pipeline de Crédito. É um componente **controlado**: não guarda
 * estado. Colunas, itens, quem está recolhido, quem está sendo arrastado e a ordem ficam em
 * quem usa; o board desenha e devolve cada gesto como output. O card entra por
 * `<ng-template #kanbanCard>` declarado no template do pai, para que ele resolva a injeção
 * de dependência pelo pai (ver README).
 *
 * `ChangeDetectionStrategy.Default` de propósito: quem usa hoje muta `Set`s no lugar e chama
 * `detectChanges()`. Um filho `OnPush` receberia a mesma referência e não se redesenharia.
 */
@Component({
  selector: 'app-kanban-board',
  standalone: true,
  imports: [NgIf, NgTemplateOutlet, DndModule, KanbanSkeletonComponent, VaultFormatCurrencyPipe],
  templateUrl: './kanban-board.component.html',
  styleUrl: './kanban-board.component.scss',
  changeDetection: ChangeDetectionStrategy.Default,
})
export class KanbanBoardComponent<
  TColumn extends KanbanBoardColumn = KanbanBoardColumn,
  TItem extends KanbanBoardItem = KanbanBoardItem,
> {
  /** Colunas já filtradas. `null` não desenha nada (nem o estado vazio). */
  @Input() columns: TColumn[] | null = null;
  /** Mostra o skeleton no lugar do board. */
  @Input() loading = false;
  @Input() skeletonColumnCount = 7;
  @Input() skeletonCardCount = 4;

  @Input() activeItemsOf: (column: TColumn) => TItem[] = () => [];
  @Input() completedItemsOf: (column: TColumn) => TItem[] = () => [];
  @Input() countOf: (column: TColumn) => number = () => 0;
  @Input() totalsOf: (column: TColumn) => KanbanColumnTotal[] = () => [];
  /** Coluna sem nenhum item (antes de qualquer filtro): ganha a dropzone que cobre o corpo. */
  @Input() isColumnEmpty: (column: TColumn) => boolean = () => false;
  @Input() isColumnLoading: (columnId: string) => boolean = () => false;
  /**
   * Texto da coluna sem nenhum item, escrito dentro da dropzone que cobre o corpo (card 3213). Vazio
   * = nada. Não acrescenta filho ao `.kanban-body`: a conta de ordem do drop continua a mesma.
   */
  @Input() emptyColumnMessageOf: (column: TColumn) => string = () => '';
  /**
   * Trava extra de arrasto por item. Vale nas duas listas desde o card 3612 (antes, só na seção de
   * concluídos): no Deals o rejeitado passou a morar numa coluna própria, na lista de ativos.
   */
  @Input() isItemDragDisabled: (item: TItem) => boolean = () => false;

  @Input() completedLabel = 'Concluídos';
  @Input() emptyMessage = '';

  @Input() minimizedColumnIds: ReadonlySet<string> = new Set();
  @Input() collapsedCompletedIds: ReadonlySet<string> = new Set();
  @Input() highlightedColumnId: string | null = null;
  @Input() receivingColumnId: string | null = null;
  @Input() enteringItemIds: ReadonlySet<string> = new Set();
  @Input() leavingItemIds: ReadonlySet<string> = new Set();
  /** Há um item sendo arrastado agora. */
  @Input() draggingActive = false;
  @Input() hasManyColumns = false;
  /**
   * Com `hasManyColumns`: as colunas esticam para ocupar a largura toda quando **nenhuma** está recolhida
   * (card 3707, pedido do Breno). Com alguma recolhida, voltam aos 235 px fixos e o quadro não fica se
   * reajustando a cada clique. Telas com poucas colunas deixavam uma faixa em branco à direita.
   */
  @Input() fillWidth = false;
  @Input() summaryMinimized = false;
  @Input() dragDisabled = false;
  /** Texto do placeholder quando o arrasto está desligado. */
  @Input() dragDisabledMessage: string | null = null;

  @ContentChild('kanbanCard') cardTemplate?: TemplateRef<KanbanCardTemplateContext<TItem, TColumn>>;
  @ContentChild('kanbanColumnHeaderPrefix')
  columnHeaderPrefixTemplate?: TemplateRef<KanbanColumnTemplateContext<TColumn>>;

  @Output() columnMinimizeToggle = new EventEmitter<KanbanColumnMinimizeEvent>();
  @Output() completedToggle = new EventEmitter<string>();
  @Output() summaryToggle = new EventEmitter<void>();
  @Output() itemDragStart = new EventEmitter<KanbanItemDragStartEvent<TItem>>();
  @Output() itemDragEnd = new EventEmitter<void>();
  @Output() columnDragOver = new EventEmitter<string>();
  @Output() columnDragLeave = new EventEmitter<string>();
  @Output() itemDrop = new EventEmitter<KanbanItemDropEvent<TColumn>>();

  isColumnMinimized(columnId: string): boolean {
    return this.minimizedColumnIds.has(columnId);
  }

  /** `fillWidth` valendo agora: muitas colunas e nenhuma recolhida. */
  get isFillingWidth(): boolean {
    return this.fillWidth && this.hasManyColumns && this.minimizedColumnIds.size === 0;
  }

  isCompletedCollapsed(columnId: string): boolean {
    return this.collapsedCompletedIds.has(columnId);
  }

  isItemEntering(itemId: string): boolean {
    return this.enteringItemIds.has(itemId);
  }

  isItemLeaving(itemId: string): boolean {
    return this.leavingItemIds.has(itemId);
  }

  isColumnReceiving(columnId: string): boolean {
    return this.receivingColumnId === columnId;
  }

  isActiveDragZone(columnId: string): boolean {
    return this.highlightedColumnId === columnId && this.draggingActive;
  }

  onMinimizeClick(columnId: string, event: Event): void {
    this.columnMinimizeToggle.emit({ columnId, event });
  }

  onDragStart(item: TItem, columnId: string): void {
    this.itemDragStart.emit({ item, columnId });
  }

  onDrop(event: DndDropEvent, column: TColumn): void {
    this.itemDrop.emit({ event, column });
  }
}
